import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Select, Button, Modal, Spin, Empty, Typography } from "antd";
import { UnorderedListOutlined, EyeOutlined } from "@ant-design/icons";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import { listCameras, listLogs, getLogsFile } from "../network/api";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import { TRAFFIC } from "../theme/themeConfig";

const { Text } = Typography;

// Human-readable bytes.
const formatBytes = (bytes) => {
  if (typeof bytes !== "number" || Number.isNaN(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const LogsViewer = () => {
  const { id } = useParams();

  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState(id || null);

  // Controlled modal state (NOT Modal.confirm — static modals don't render on React 19).
  const [viewing, setViewing] = useState(null); // filename | null
  const [logContent, setLogContent] = useState("");
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const res = await listCameras({ page: 1, pageSize: 200 });
        setCameras(res?.data || []);
      } catch (e) {
        notify.error(serverError(e, "Failed to load devices"));
      }
    };
    fetchCameras();
  }, []);

  useEffect(() => {
    if (id) setDeviceId(id);
  }, [id]);

  const deviceOptions = useMemo(
    () =>
      cameras.map((cam) => ({
        value: cam.deviceId,
        label: cam.deviceTitle ? `${cam.deviceId} (${cam.deviceTitle})` : cam.deviceId,
      })),
    [cameras]
  );

  const openLog = async (filename) => {
    setViewing(filename);
    setLogContent("");
    setLogLoading(true);
    try {
      const content = await getLogsFile(deviceId, filename);
      setLogContent(typeof content === "string" ? content : JSON.stringify(content, null, 2));
    } catch (e) {
      notify.error(serverError(e, "Failed to load log file"));
      setLogContent("");
    } finally {
      setLogLoading(false);
    }
  };

  const closeLog = () => {
    setViewing(null);
    setLogContent("");
  };

  const columns = [
    {
      title: "File name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 140,
      sorter: true,
      render: (size) => formatBytes(size),
    },
    {
      title: "Modified",
      dataIndex: "mtime",
      key: "mtime",
      width: 220,
      sorter: true,
      render: (mtime) =>
        mtime ? relativeTime(mtime) : <Text type="secondary">—</Text>,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => openLog(record.name)}
        >
          View
        </Button>
      ),
    },
  ];

  const deviceSelect = (
    <Select
      showSearch
      allowClear
      placeholder="Select a device"
      style={{ minWidth: 260 }}
      value={deviceId || undefined}
      onChange={(v) => setDeviceId(v || null)}
      options={deviceOptions}
      optionFilterProp="label"
    />
  );

  return (
    <div>
      <PageHeader
        title="Logs"
        subtitle="Device log files"
        icon={<UnorderedListOutlined style={{ fontSize: 22, color: TRAFFIC.yellow }} />}
        extra={deviceSelect}
      />

      {deviceId ? (
        <DataTable
          key={deviceId}
          rowKey="name"
          columns={columns}
          fetcher={(p) => listLogs(deviceId, p)}
          searchPlaceholder="Search log files…"
          defaultPageSize={20}
          scrollX={620}
          emptyText="No logs for this device"
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a device to view its logs"
          style={{ marginTop: 64 }}
        />
      )}

      <Modal
        open={!!viewing}
        title={viewing || "Log file"}
        onCancel={closeLog}
        footer={[
          <Button key="close" onClick={closeLog}>
            Close
          </Button>,
        ]}
        width="80vw"
        styles={{ body: { maxHeight: "70vh", overflow: "auto" } }}
        destroyOnClose
      >
        {logLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin />
          </div>
        ) : (
          <pre
            style={{
              background: "#0b1f3a",
              color: "#e6edf7",
              padding: 16,
              borderRadius: 8,
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {logContent || "Empty log file"}
          </pre>
        )}
      </Modal>
    </div>
  );
};

export default LogsViewer;
